import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { IconButton, TextField } from '@material-ui/core';
import { Add, Clear } from '@material-ui/icons';
import {
  compose, filter, get, identity, includes, pickBy, size,
} from 'lodash/fp';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FastField, Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  Button, ContactSwapButton, Dialog,
  DialogTitle, DialogContent, DialogActions,
  GroupField, Select, PhoneNumberField,
} from '../../../common/components';
import messages from '../../messages';
import {
  regexPhoneNumber, regexPhoneNumberOrEmpty,
} from '../../../common/utils/validation';

const relationships = [
  { id: '1', label: '부', value: 'father' },
  { id: '2', label: '모', value: 'mother' },
  { id: '3', label: '조부', value: 'grand_father' },
  { id: '4', label: '조모', value: 'grand_mother' },
  { id: '5', label: '기타', value: 'etc' },
];

const S = {
  Dialog: styled(Dialog)`
    .paper {
      width: 600px;
    }
  `,
  Row: styled.div`
    display: flex;
    justify-content: ${({ justify }) => justify};
    align-items: ${({ alignItems }) => alignItems || 'center'};
    margin-top: ${({ margin = {} }) => margin.top};
    margin-left: ${({ margin = {} }) => margin.left};
    margin-bottom: ${({ margin = {} }) => margin.bottom};
    margin-right: ${({ margin = {} }) => margin.right};
  `,
  Item: styled.div`
    width: 50%;
    :first-child {
      padding-right: 8px;
    }
    :last-child {
      padding-left: 8px;
    }
  `,
  Heading: styled.h3`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(20)};
    font-weight: 500;
    line-height: 1.5;
  `,
  Clear: styled(Clear)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 32px;
  `,
  Fieldset: styled.fieldset`
    min-width: auto;
    max-width: 100%;
    margin: 0 0 24px;
    padding: 0;
    border: none;
    :nth-child(3) {
      margin-bottom: 40px;
    }
  `,
  Legend: styled.legend`
    padding: 0;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 500;
  `,
  GroupField: styled(GroupField)`
    width: 100%;
  `,
  TextField: styled(({ InputLabelProps, ...otherProps }) => (
    <TextField
      InputLabelProps={{
        ...InputLabelProps,
        classes: { root: 'label-root' },
      }}
      {...otherProps}
    />
  ))`
    width: 100%;
    margin: 0;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    .label-root {
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.palette.grey[300]};
    }
  `,
  Select: styled(Select)`
    width: 100%;
  `,
  Note: styled.p`
    margin-left: 8px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
  `,
  AddButton: styled(Button)`
    margin-bottom: 40px;
    padding: 8px 16px 9px;
    line-height: 1.53;
  `,
  Add: styled(Add)`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[700]};
    font-size: 1.25rem;
  `,
  DeleteButton: styled.button`
    margin-left: 8px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 500;
    line-height: 1.54;
    cursor: pointer;
    :hover {
      color: ${({ theme }) => theme.palette.error.main};
      text-decoration: underline;
    }
  `,
  CancelButton: styled(Button)`
    min-height: 40px;
    color: ${({ theme }) => theme.palette.grey[700]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  SaveButton: styled(Button)`
    min-height: 40px;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
};

const ContactAddDialog = ({
  creationTask, groups, initCreationTask, intl, match, open, onClose, onSubmit,
}) => {
  const [visible, setVisible] = useState(false);

  const handleSubmit = useCallback(values => onSubmit({ values, sid: match.params.sid }), [match.params.sid, onSubmit]);
  const showSecondaryGuardianFieldset = useCallback(() => setVisible(true), []);
  const hideSecondaryGuardianFieldset = useCallback(() => setVisible(false), []);
  const validateGroups = useCallback((value) => {
    const clazzGroupCount = compose(
      size,
      filter(v => includes('clazzGroup', v.id)),
    )(value);

    return clazzGroupCount > 1 && '학년∙반은 하나만 선택할 수 있습니다.';
  }, []);

  const Schema = useMemo(() => Yup.object().shape(
    pickBy(identity, {
      student: Yup.object({
        groups: Yup.array().of(Yup.object({
          type: Yup.string(),
          value: Yup.number(),
        }))
          .required(),
        name: Yup.string()
          .max(8)
          .required(),
        clazzNumber: Yup.string(),
        phoneNumber: Yup.string().matches(regexPhoneNumberOrEmpty),
        email: Yup.string().email(),
      }),
      primaryGuardian: Yup.object({
        name: Yup.string()
          .max(8)
          .required(),
        relationship: Yup.string(),
        phoneNumber: Yup.string()
          .matches(regexPhoneNumber)
          .required(),
        email: Yup.string().email(),
      }),
      secondaryGuardian: visible && Yup.object({
        name: Yup.string()
          .max(8)
          .required(),
        relationship: Yup.string(),
        phoneNumber: Yup.string()
          .matches(regexPhoneNumber)
          .required(),
        email: Yup.string().email(),
      }),
    }),
  ),
  [visible]);

  useEffect(() => {
    if (creationTask.data) {
      onClose();

      const timeoutId = setTimeout(initCreationTask, 3000);

      return () => clearTimeout(timeoutId);
    }

    return () => {};
  }, [creationTask.data, initCreationTask, onClose]);

  return (
    <S.Dialog open={open} onClose={onClose} onExited={hideSecondaryGuardianFieldset}>
      <S.Row justify="space-between" margin={{ top: '-3px', bottom: '29px' }}>
        <DialogTitle>
          <S.Heading>
            <FormattedMessage {...messages.contactAddDialogHeading} />
          </S.Heading>
        </DialogTitle>
        <IconButton type="button" size="small" onClick={onClose}>
          <S.Clear />
        </IconButton>
      </S.Row>
      <Formik
        initialValues={{
          student: {
            groups: [],
            name: '',
            clazzNumber: '',
            phoneNumber: '',
            email: '',
          },
          primaryGuardian: {
            name: '',
            relationship: '',
            phoneNumber: '',
            email: '',
          },
          secondaryGuardian: {
            name: '',
            relationship: '',
            phoneNumber: '',
            email: '',
          },
        }}
        validationSchema={Schema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, setValues }) => (
          <Form>
            <DialogContent>
              <S.Fieldset>
                <S.Row margin={{ bottom: '8px' }}>
                  <S.Legend>
                    <FormattedMessage {...messages.contactAddDialogLegendStudent} />
                  </S.Legend>
                </S.Row>
                <S.Row>
                  <FastField
                    name="student.groups"
                    validate={validateGroups}
                  >
                    {({ field, ...otherProps }) => (
                      <S.GroupField
                        label={intl.formatMessage(messages.contactAddDialogGroupFieldLabel)}
                        groups={groups}
                        required
                        field={field}
                        {...otherProps}
                      />
                    )}
                  </FastField>
                </S.Row>
                <S.Row justify="space-between" margin={{ bottom: '16px' }}>
                  <S.Item>
                    <FastField name="student.name">
                      {({ field, form, ...otherProps }) => (
                        <S.TextField
                          label={intl.formatMessage(messages.contactAddDialogTextFieldStudentName)}
                          margin="dense"
                          variant="outlined"
                          error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                          {...field}
                          {...otherProps}
                        />
                      )}
                    </FastField>
                  </S.Item>
                  <S.Item>
                    <FastField name="student.clazzNumber">
                      {({ field, form, ...otherProps }) => (
                        <S.TextField
                          label={intl.formatMessage(messages.contactAddDialogTextFieldClazzNumber)}
                          margin="dense"
                          variant="outlined"
                          error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                          {...field}
                          {...otherProps}
                        />
                      )}
                    </FastField>
                  </S.Item>
                </S.Row>
                <S.Row justify="space-between">
                  <S.Item>
                    <FastField
                      name="student.phoneNumber"
                      component={PhoneNumberField}
                      label={intl.formatMessage(messages.contactAddDialogTextFieldStudentPhoneNumber)}
                    />
                  </S.Item>
                  <S.Item>
                    <FastField name="student.email">
                      {({ field, form, ...otherProps }) => (
                        <S.TextField
                          type="email"
                          label={intl.formatMessage(messages.contactAddDialogTextFieldStudentEmail)}
                          margin="dense"
                          variant="outlined"
                          error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                          {...field}
                          {...otherProps}
                        />
                      )}
                    </FastField>
                  </S.Item>
                </S.Row>
              </S.Fieldset>
              <S.Fieldset>
                <S.Row alignItems="flex-end" margin={{ bottom: '8px' }}>
                  <S.Legend>
                    <FormattedMessage {...messages.contactAddDialogLegendPrimaryGuardian} />
                  </S.Legend>
                  <S.Note>
                    <FormattedMessage {...messages.contactAddDialogNotePrimaryGuardian} />
                  </S.Note>
                </S.Row>
                <S.Row justify="space-between" margin={{ bottom: '16px' }}>
                  <S.Item>
                    <FastField name="primaryGuardian.name">
                      {({ field, form, ...otherProps }) => (
                        <S.TextField
                          label={intl.formatMessage(messages.contactAddDialogTextFieldPrimaryGuardianName)}
                          margin="dense"
                          variant="outlined"
                          error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                          {...field}
                          {...otherProps}
                        />
                      )}
                    </FastField>
                  </S.Item>
                  <S.Item>
                    <FastField name="primaryGuardian.relationship">
                      {({ field, form, ...otherProps }) => (
                        <S.Select
                          label={intl.formatMessage(messages.contactAddDialogTextFieldPrimaryGuardianRelationship)}
                          options={relationships}
                          error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                          {...field}
                          {...otherProps}
                        />
                      )}
                    </FastField>
                  </S.Item>
                </S.Row>
                <S.Row justify="space-between">
                  <S.Item>
                    <FastField
                      name="primaryGuardian.phoneNumber"
                      component={PhoneNumberField}
                      label={intl.formatMessage(messages.contactAddDialogTextFieldPrimaryGuardianPhoneNumber)}
                    />
                  </S.Item>
                  <S.Item>
                    <FastField name="primaryGuardian.email">
                      {({ field, form, ...otherProps }) => (
                        <S.TextField
                          type="email"
                          label={intl.formatMessage(messages.contactAddDialogTextFieldPrimaryGuardianEmail)}
                          margin="dense"
                          variant="outlined"
                          error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                          {...field}
                          {...otherProps}
                        />
                      )}
                    </FastField>
                  </S.Item>
                </S.Row>
              </S.Fieldset>
              {visible && (
                <S.Fieldset>
                  <S.Row alignItems="flex-end" margin={{ bottom: '8px' }}>
                    <S.Legend>
                      <FormattedMessage {...messages.contactAddDialogLegendSecondaryGuardian} />
                    </S.Legend>
                    <S.DeleteButton
                      type="button"
                      onClick={() => {
                        hideSecondaryGuardianFieldset();
                        setFieldValue('secondaryGuardian', {
                          name: '',
                          relationship: '',
                          phoneNumber: '',
                          email: '',
                        });
                      }}
                    >
                      <FormattedMessage {...messages.contactAddDialogDeleteButton} />
                    </S.DeleteButton>
                  </S.Row>
                  <S.Row justify="space-between" margin={{ bottom: '16px' }}>
                    <S.Item>
                      <FastField name="secondaryGuardian.name">
                        {({ field, form, ...otherProps }) => (
                          <S.TextField
                            label={intl.formatMessage(messages.contactAddDialogTextFieldSecondaryGuardianName)}
                            margin="dense"
                            variant="outlined"
                            error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                            {...field}
                            {...otherProps}
                          />
                        )}
                      </FastField>
                    </S.Item>
                    <S.Item>
                      <FastField name="secondaryGuardian.relationship">
                        {({ field, form, ...otherProps }) => (
                          <S.Select
                            label={intl.formatMessage(messages.contactAddDialogTextFieldSecondaryGuardianRelationship)}
                            options={relationships}
                            error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                            {...field}
                            {...otherProps}
                          />
                        )}
                      </FastField>
                    </S.Item>
                  </S.Row>
                  <S.Row justify="space-between">
                    <S.Item>
                      <FastField
                        name="secondaryGuardian.phoneNumber"
                        component={PhoneNumberField}
                        label={intl.formatMessage(messages.contactAddDialogTextFieldSecondaryGuardianPhoneNumber)}
                      />
                    </S.Item>
                    <S.Item>
                      <FastField name="secondaryGuardian.email">
                        {({ field, form, ...otherProps }) => (
                          <S.TextField
                            type="email"
                            label={intl.formatMessage(messages.contactAddDialogTextFieldSecondaryGuardianEmail)}
                            margin="dense"
                            variant="outlined"
                            error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                            {...field}
                            {...otherProps}
                          />
                        )}
                      </FastField>
                    </S.Item>
                  </S.Row>
                </S.Fieldset>
              )}
              {!visible && (
                <S.AddButton type="button" onClick={showSecondaryGuardianFieldset}>
                  <S.Add />
                  <FormattedMessage {...messages.contactAddDialogAddButton} />
                </S.AddButton>
              )}
            </DialogContent>
            <DialogActions>
              {visible && (
                <ContactSwapButton values={values} onClick={setValues}>
                  <FormattedMessage {...messages.contactAddDialogContactSwapButton} />
                </ContactSwapButton>
              )}
              <S.CancelButton type="button" variant="outlined" onClick={onClose}>
                <FormattedMessage {...messages.contactAddDialogCancelButton} />
              </S.CancelButton>
              <S.SaveButton type="submit" color="primary" variant="contained">
                <FormattedMessage {...messages.contactAddDialogSaveButton} />
              </S.SaveButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </S.Dialog>
  );
};

ContactAddDialog.propTypes = {
  creationTask: PropTypes.shape({ data: null, loading: false, error: null }).isRequired,
  groups: PropTypes.shape({
    clazzGroups: PropTypes.arrayOf(PropTypes.shape({})),
    customGroups: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initCreationTask: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

ContactAddDialog.defaultProps = {
  groups: null,
  onClose: () => {},
  onSubmit: () => {},
};

export default compose(
  withRouter,
  injectIntl,
)(ContactAddDialog);
